// /voice-clones — upload, list, rename, delete user voice clones.
// Upload form does client-side base64 encoding + duration probing via
// HTMLAudioElement metadata so we don't need extra libs.

import { useEffect, useRef, useState } from 'react';
import { ApiError, voiceClones, type ConsentType, type VoiceCloneSummary } from '../api.js';

const CONSENT_OPTIONS: { id: ConsentType; label: string; help: string }[] = [
  {
    id: 'own_voice',
    label: 'This is my own voice',
    help: 'You are recording yourself or using a sample of your own voice.',
  },
  {
    id: 'licensed',
    label: 'I have a license to use this voice',
    help: 'Voice actor under contract, licensed stock voice, etc.',
  },
  {
    id: 'signed_release',
    label: 'I have a signed release from the voice owner',
    help: 'Friend, family member, talent who signed a release form.',
  },
  {
    id: 'public_domain',
    label: 'This voice is in the public domain',
    help: 'Historic recording with no living copyright holder.',
  },
  {
    id: 'ai_synthetic',
    label: 'This is an AI-synthetic voice',
    help: 'Reproducing an AI voice you generated yourself.',
  },
];

const ACCEPTED_MIME = ['audio/wav', 'audio/mpeg', 'audio/ogg', 'audio/flac'] as const;
type AcceptedMime = (typeof ACCEPTED_MIME)[number];

function fmtBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function probeAudioMetadata(file: File): Promise<{ durationMs: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.src = url;
    audio.onloadedmetadata = () => {
      const durationMs = Math.round(audio.duration * 1000);
      URL.revokeObjectURL(url);
      resolve({ durationMs });
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('could not read audio metadata'));
    };
  });
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip "data:audio/wav;base64," prefix
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.readAsDataURL(file);
  });
}

export function VoiceClonesPage() {
  const [list, setList] = useState<VoiceCloneSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const r = await voiceClones.list();
      setList(r.clones);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load.');
      setList([]);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete the clone "${name}"? The original audio sample will be permanently removed.`)) return;
    try {
      await voiceClones.remove(id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  }

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Voice clones</h1>
          <p className="text-muted text-sm">
            Upload a 3–60 second sample to clone any voice. Works with Chatterbox today;
            ElevenLabs &amp; Cartesia coming next.
          </p>
        </div>
        <button
          onClick={() => setUploadOpen(true)}
          className="bg-ink text-parchment px-4 py-2 rounded-md hover:bg-ink/90 transition-colors text-sm"
        >
          Upload clone
        </button>
      </div>

      {error && (
        <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {uploadOpen && (
        <UploadForm
          onClose={() => setUploadOpen(false)}
          onDone={() => {
            setUploadOpen(false);
            void refresh();
          }}
        />
      )}

      {list === null ? (
        <p className="text-muted text-sm">loading…</p>
      ) : list.length === 0 ? (
        <div className="border border-soft rounded-lg p-12 text-center">
          <p className="text-muted mb-2">No voice clones yet.</p>
          <p className="text-xs text-muted/70">
            Use any cloning-capable provider (Chatterbox, ElevenLabs, Cartesia, Fish) by picking
            <span className="font-mono"> clone:&lt;id&gt;</span> as the voice ID.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((c) => (
            <li
              key={c.id}
              className="border border-soft rounded-lg px-5 py-4 bg-white"
            >
              <div className="flex items-baseline justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="font-serif text-lg truncate">{c.name}</h2>
                  <div className="text-xs text-muted/80 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span>{fmtDuration(c.duration_ms)}</span>
                    <span>·</span>
                    <span>{fmtBytes(c.size_bytes)}</span>
                    <span>·</span>
                    <span className="font-mono">{c.mime_type}</span>
                    <span>·</span>
                    <span>consent: {c.consent_type.replace('_', ' ')}</span>
                  </div>
                  {c.description && (
                    <p className="text-sm text-muted mt-2">{c.description}</p>
                  )}
                  <div className="text-xs text-muted/60 mt-2 font-mono">
                    voice id: clone:{c.id}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a
                    href={voiceClones.sourceUrl(c.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted hover:text-ink px-2 py-1"
                  >
                    Play sample
                  </a>
                  <button
                    onClick={() => handleDelete(c.id, c.name)}
                    className="text-xs text-muted hover:text-accent px-2 py-1"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UploadForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [consent, setConsent] = useState<ConsentType>('own_voice');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setDurationMs(null);
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type as AcceptedMime)) {
      setError(`Unsupported file type "${f.type}". Use WAV, MP3, OGG, or FLAC.`);
      setFile(null);
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      setError('File is larger than 30 MB.');
      setFile(null);
      return;
    }
    try {
      const { durationMs } = await probeAudioMetadata(f);
      setDurationMs(durationMs);
      if (durationMs < 3000) {
        setError(`Audio is too short (${(durationMs / 1000).toFixed(1)}s). Need at least 3 seconds.`);
      } else if (durationMs > 5 * 60 * 1000) {
        setError(`Audio is too long (${Math.round(durationMs / 1000)}s). Keep it under 5 minutes.`);
      }
    } catch {
      setError('Could not read audio metadata. File may be corrupt.');
      setFile(null);
    }
  }

  async function handleSubmit() {
    if (!file || !durationMs || !consentAccepted || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const audio_base64 = await readFileAsBase64(file);
      await voiceClones.create({
        name: name.trim(),
        description: description.trim() || null,
        audio_base64,
        mime_type: file.type as AcceptedMime,
        duration_ms: durationMs,
        consent_type: consent,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DUPLICATE_CLONE') {
        setError('You already uploaded this exact audio. Pick a different sample.');
      } else {
        setError(err instanceof ApiError ? err.message : 'Upload failed.');
      }
      setSubmitting(false);
    }
  }

  const consentInfo = CONSENT_OPTIONS.find((c) => c.id === consent)!;
  const canSubmit = !!file && !!durationMs && durationMs >= 3000 && durationMs <= 5 * 60 * 1000 && consentAccepted && name.trim().length > 0 && !submitting;

  return (
    <div className="border border-soft rounded-lg bg-white px-5 py-5 mb-4">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-xl">Upload a voice sample</h2>
        <button onClick={onClose} className="text-xs text-muted hover:text-ink">Cancel</button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-muted block mb-1">Audio file (WAV, MP3, OGG, or FLAC · 3–60 s · &lt;30 MB)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME.join(',')}
            onChange={handleFileChange}
            className="w-full text-sm"
          />
          {file && durationMs && (
            <div className="text-xs text-muted mt-1">
              {file.name} · {fmtBytes(file.size)} · {fmtDuration(durationMs)}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs text-muted block mb-1">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. "My narration voice"'
            maxLength={80}
            className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        <div>
          <label className="text-xs text-muted block mb-1">Description (optional)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='e.g. "warm, conversational, for audiobook chapters"'
            maxLength={500}
            className="w-full px-3 py-2 bg-parchment/60 border border-soft rounded focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        <div>
          <label className="text-xs text-muted block mb-2">Source of the voice</label>
          <div className="space-y-1.5">
            {CONSENT_OPTIONS.map((opt) => (
              <label key={opt.id} className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="consent"
                  value={opt.id}
                  checked={consent === opt.id}
                  onChange={() => setConsent(opt.id)}
                  className="mt-0.5"
                />
                <span>
                  {opt.label}
                  <span className="block text-xs text-muted">{opt.help}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-soft/40 border border-soft rounded p-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I confirm: <strong>{consentInfo.label.toLowerCase()}</strong>. I take full responsibility
              for use of this voice and understand that all clones include an inaudible AI watermark.
            </span>
          </label>
        </div>

        {error && (
          <div className="text-xs text-accent bg-accent/5 border border-accent/20 rounded px-3 py-2">
            {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-accent text-parchment px-4 py-2 rounded text-sm hover:bg-accent/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? 'Uploading…' : 'Upload clone'}
        </button>
      </div>
    </div>
  );
}
